import { Actions, handleApiRequest, useMobile } from "@cpj-ai/panel-npm-package";
import {
  VerifyOrganizationMutation,
  VerifyOrganizationMutationVariables,
  useVerifyOrganizationMutation,
} from "@graphql/generated";
import { InputOtp } from "@heroui/react";
import { useOrganizationStore } from "@store/useOrganizationStore";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";

interface IEnterOtpCodeStep {
  handleNextStep: () => void;
  phoneNumber: string;
}

const EnterOtpCodeStep = ({ handleNextStep, phoneNumber }: IEnterOtpCodeStep) => {
  const { selectedOrganization } = useOrganizationStore();
  const isMobile = useMobile();
  const [verifyOrganization, { loading }] = useVerifyOrganizationMutation();
  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      otp: "",
    },
  });

  const handleVerify = useCallback(async () => {
    if (!selectedOrganization?.id) {
      toast.error("No organization selected");
      return;
    }

    await handleApiRequest<VerifyOrganizationMutation, VerifyOrganizationMutationVariables>({
      apiFunction: verifyOrganization,
      variables: {
        organization: selectedOrganization.id,
        otp: getValues().otp,
      },
      componentName: "EnterOtpCodeStep",
      actionName: "verifyOrganization",
      onSuccess: (response) => {
        if (response.data?.account.organization?.verify?.success) {
          handleNextStep();
          toast.success("Organization verified successfully");
        } else {
          toast.error("Invalid OTP code. Please try again");
        }
      },
      displaySuccessToast: false,
      fallbackErrorMessage: "Failed to verify organization",
      extraErrorContext: {
        organizationId: selectedOrganization.id,
      },
    });
  }, [getValues, handleNextStep, selectedOrganization?.id, verifyOrganization]);

  return (
    <div className="flex flex-col justify-center mt-3">
      <p className="text-sm text-zinc-500 text-center mt-6 md:mt-0">
        We have sent you a 6-digit code for authentication and communication. Please enter it below
      </p>
      <p className="text-sm text-zinc-500 text-center mt-2">
        Phone Number: <span className="font-bold">{phoneNumber}</span>
      </p>
      <div className="flex flex-col justify-center items-center mt-6">
        <Controller
          control={control}
          name="otp"
          render={({ field }) => (
            <InputOtp
              {...field}
              errorMessage={errors?.otp?.message}
              autoFocus
              size={isMobile ? "md" : "lg"}
              isInvalid={!!errors.otp}
              length={6}
              radius="lg"
              placeholder="-"
              variant="bordered"
              classNames={{
                segmentWrapper: "gap-x-2 md:gap-x-4",
              }}
            />
          )}
          rules={{
            required: "OTP is required",
            minLength: {
              value: 6,
              message: "Please enter a valid OTP",
            },
          }}
        />

        <Actions
          className="mt-6 w-full md:!w-fit !grid-cols-1"
          showCancel={false}
          submitProps={{
            onClick: handleSubmit(handleVerify),
            className: "w-full md:w-fit px-12 block mx-auto",
            isLoading: loading,
            isDisabled: loading,
            children: "Verify Code",
          }}
        />
      </div>
    </div>
  );
};

export default EnterOtpCodeStep;
